import React, { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Icon, Spinner } from '../';

import { AssetCard } from '../AssetCard';
import { FolderCard } from '../FolderCard';

import type { AssetBrowserProps, PaginationInfo } from './AssetBrowser.types';
import type { Asset } from '../../types/asset';
import type { Folder } from '../../types/folder';

const BrowserContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const BrowserWrapper = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
`;

const BrowserContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const GridLayout = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(1, 1fr);

  @media (min-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 992px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ListLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & > * {
    width: 100%;
  }
`;

const SectionDivider = styled.div`
  height: 1px;
  background-color: #f5f5f5;
  margin: 24px 0;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
  border-radius: 8px;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  text-align: center;
  color: rgba(0, 0, 0, 0.7);
  min-height: 200px;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  color: #949494;
  margin-bottom: 16px;
`;

const EmptyStateMessage = styled.p`
  font-size: 14px;
  margin: 0;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #f5f5f5;
  background-color: #fff;
`;

const PaginationButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid #f5f5f5;
  border-radius: 8px;
  background-color: ${({ $active }) => ($active ? 'var(--color-highlight, var(--colour-highlight, #F68D32))' : '#fff')};
  color: ${({ $active }) => ($active ? 'var(--color-highlight-text, var(--colour-highlight-text, #E7E7E7))' : 'rgba(0, 0, 0, 0.87)')};
  cursor: pointer;
  transition: all 0.2s ease-out;

  &:hover:not(:disabled) {
    background-color: ${({ $active }) => ($active ? 'var(--color-highlight, var(--colour-highlight, #F68D32))' : '#f5f5f5')};
    border-color: #e0e0e0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationPages = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Ellipsis = styled.span`
  color: #949494;
  padding: 0 4px;
`;

const CardWrapper = styled.div`
  height: 100%;
`;

/**
 * Generate visible page numbers for pagination
 */
const getVisiblePages = (
  currentPage: number,
  totalPages: number,
  maxVisible: number = 5
): (number | 'ellipsis')[] => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const halfVisible = Math.floor(maxVisible / 2);

  let startPage = Math.max(1, currentPage - halfVisible);
  let endPage = Math.min(totalPages, currentPage + halfVisible);

  if (currentPage <= halfVisible) {
    endPage = maxVisible - 1;
  } else if (currentPage >= totalPages - halfVisible) {
    startPage = totalPages - maxVisible + 2;
  }

  if (startPage > 1) {
    pages.push(1);
    if (startPage > 2) {
      pages.push('ellipsis');
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      pages.push('ellipsis');
    }
    pages.push(totalPages);
  }

  return pages;
};

/**
 * Pagination component
 */
const Pagination: React.FC<{
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
}> = ({ pagination, onPageChange }) => {
  const { currentPage, totalPages } = pagination;
  const visiblePages = getVisiblePages(currentPage, totalPages);

  const handleFirst = () => onPageChange(1);
  const handlePrev = () => onPageChange(Math.max(1, currentPage - 1));
  const handleNext = () => onPageChange(Math.min(totalPages, currentPage + 1));
  const handleLast = () => onPageChange(totalPages);

  return (
    <PaginationContainer role="navigation" aria-label="Pagination">
      <PaginationButton
        onClick={handleFirst}
        disabled={currentPage === 1}
        aria-label="Go to first page"
      >
        <Icon name="keyboard_double_arrow_left" size="sm" />
      </PaginationButton>
      <PaginationButton
        onClick={handlePrev}
        disabled={currentPage === 1}
        aria-label="Go to previous page"
      >
        <Icon name="keyboard_arrow_left" size="sm" />
      </PaginationButton>

      <PaginationPages>
        {visiblePages.map((page, index) =>
          page === 'ellipsis' ? (
            <Ellipsis key={`ellipsis-${index}`}>…</Ellipsis>
          ) : (
            <PaginationButton
              key={page}
              $active={page === currentPage}
              onClick={() => onPageChange(page)}
              aria-label={`Go to page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </PaginationButton>
          )
        )}
      </PaginationPages>

      <PaginationButton
        onClick={handleNext}
        disabled={currentPage === totalPages}
        aria-label="Go to next page"
      >
        <Icon name="keyboard_arrow_right" size="sm" />
      </PaginationButton>
      <PaginationButton
        onClick={handleLast}
        disabled={currentPage === totalPages}
        aria-label="Go to last page"
      >
        <Icon name="keyboard_double_arrow_right" size="sm" />
      </PaginationButton>
    </PaginationContainer>
  );
};

/**
 * Empty state component
 */
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <EmptyStateContainer>
    <EmptyStateIcon>
      <Icon name="folder_off" size="lg" />
    </EmptyStateIcon>
    <EmptyStateMessage>{message}</EmptyStateMessage>
  </EmptyStateContainer>
);

/**
 * AssetBrowser component for browsing folders and assets
 * Combines FolderCard and AssetCard with grid/list layouts and pagination
 */
export const AssetBrowser: React.FC<AssetBrowserProps> = ({
  folders,
  assets,
  layout = 'grid',
  columns,
  loading = false,
  emptyMessage = 'No items to display',
  selectedIds = [],
  selectionMode = 'none',
  onFolderClick,
  onAssetClick,
  onSelectionChange,
  onDownload,
  onInsertLink,
  pagination,
  onPageChange,
  className = '',
  'data-testid': testId,
  dataTestId,
  dataId,
}) => {
  const isEmpty = folders.length === 0 && assets.length === 0;

  // Handle item selection
  const handleSelect = useCallback(
    (id: string) => {
      if (selectionMode === 'none' || !onSelectionChange) {
        return;
      }

      if (selectionMode === 'single') {
        onSelectionChange([id]);
      } else {
        const newSelection = selectedIds.includes(id)
          ? selectedIds.filter((selectedId) => selectedId !== id)
          : [...selectedIds, id];
        onSelectionChange(newSelection);
      }
    },
    [selectionMode, selectedIds, onSelectionChange]
  );

  // Handle folder click
  const handleFolderClick = useCallback(
    (folder: Folder) => {
      if (onFolderClick) {
        onFolderClick(folder);
      }
    },
    [onFolderClick]
  );

  // Handle folder selection
  const handleFolderSelect = useCallback(
    (id: string) => {
      handleSelect(id);
    },
    [handleSelect]
  );

  // Handle asset click
  const handleAssetClick = useCallback(
    (asset: Asset) => {
      handleSelect(asset.id);
      if (onAssetClick) {
        onAssetClick(asset);
      }
    },
    [handleSelect, onAssetClick]
  );

  // Handle asset download
  const handleDownload = useCallback(
    (id: string) => {
      const asset = assets.find((a) => a.id === id);
      if (asset && onDownload) {
        onDownload(asset);
      }
    },
    [assets, onDownload]
  );

  // Handle insert link
  const handleInsertLink = useCallback(
    (id: string) => {
      const asset = assets.find((a) => a.id === id);
      if (asset && onInsertLink) {
        onInsertLink(asset);
      }
    },
    [assets, onInsertLink]
  );

  // Calculate grid style based on columns prop
  const gridStyle = useMemo(() => {
    if (layout !== 'grid' || !columns) {
      return undefined;
    }

    if (typeof columns === 'number') {
      return { gridTemplateColumns: `repeat(${columns}, 1fr)` };
    }

    return undefined;
  }, [layout, columns]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!pagination || !onPageChange) return;

      const { currentPage, totalPages } = pagination;

      switch (e.key) {
        case 'ArrowLeft':
          if (currentPage > 1) {
            onPageChange(currentPage - 1);
          }
          break;
        case 'ArrowRight':
          if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
          }
          break;
        case 'Home':
          onPageChange(1);
          break;
        case 'End':
          onPageChange(totalPages);
          break;
      }
    },
    [pagination, onPageChange]
  );

  const hasFoldersAndAssets = folders.length > 0 && assets.length > 0;
  const LayoutComponent = layout === 'grid' ? GridLayout : ListLayout;

  return (
    <BrowserContainer
      className={className}
      data-testid={dataTestId || testId}
      data-id={dataId}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <BrowserWrapper>
        {loading && (
          <LoadingOverlay data-testid="loading-overlay">
            <Spinner size="lg" label="Loading items..." />
          </LoadingOverlay>
        )}

        <BrowserContent>
          {isEmpty && !loading ? (
            <EmptyState message={emptyMessage} />
          ) : (
            <>
              {/* Folders section */}
              {folders.length > 0 && (
                <LayoutComponent style={gridStyle} role="list" aria-label="Folders">
                  {folders.map((folder) => (
                    <CardWrapper key={folder.id} role="listitem">
                      <FolderCard
                        folder={folder}
                        layout={layout === 'list' ? 'list' : 'card'}
                        selected={selectedIds.includes(folder.id)}
                        onSelect={handleFolderSelect}
                        onClick={handleFolderClick}
                        onDoubleClick={handleFolderClick}
                        data-testid={`folder-${folder.id}`}
                      />
                    </CardWrapper>
                  ))}
                </LayoutComponent>
              )}

              {/* Divider between folders and assets */}
              {hasFoldersAndAssets && <SectionDivider />}

              {/* Assets section */}
              {assets.length > 0 && (
                <LayoutComponent style={gridStyle} role="list" aria-label="Assets">
                  {assets.map((asset) => (
                    <CardWrapper key={asset.id} role="listitem">
                      <AssetCard
                        asset={asset}
                        selected={selectedIds.includes(asset.id)}
                        onSelect={() => handleAssetClick(asset)}
                        onDownload={onDownload ? handleDownload : undefined}
                        onInsertLink={onInsertLink ? handleInsertLink : undefined}
                        data-testid={`asset-${asset.id}`}
                      />
                    </CardWrapper>
                  ))}
                </LayoutComponent>
              )}
            </>
          )}
        </BrowserContent>
      </BrowserWrapper>

      {pagination && onPageChange && pagination.totalPages > 1 && (
        <Pagination pagination={pagination} onPageChange={onPageChange} />
      )}
    </BrowserContainer>
  );
};

AssetBrowser.displayName = 'AssetBrowser';